import { IfAbility } from '@efdigital/ll-addon-auth';
import type { CoachingConfig } from '@efdigital/ll-addon-data/types/coaching/coaching-config';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BsDropdown from 'ember-bootstrap/components/bs-dropdown';

import eq from '../../helpers/eq.ts';
import gt from '../../helpers/gt.ts';
import CoachingConfigRow from './coaching-config-row.gts';

const PAGE_SIZE = 12;

type ConfigSort = 'dateAdded' | 'name';

function sortConfigs(
  configs: CoachingConfig[],
  sortBy: ConfigSort
): CoachingConfig[] {
  const sorted = [...configs];

  if (sortBy === 'name') {
    return sorted.sort((a, b) => a.name.localeCompare(b.name));
  }

  return sorted.sort((a, b) => {
    const aTime = a.modified ? new Date(a.modified).getTime() : 0;
    const bTime = b.modified ? new Date(b.modified).getTime() : 0;

    return bTime - aTime;
  });
}

interface CoachingConfigsBrowseSignature {
  Args: {
    coachingConfigs: CoachingConfig[];
  };
  Element: HTMLDivElement;
}

export class CoachingConfigsBrowse extends Component<CoachingConfigsBrowseSignature> {
  @tracked currentPage = 1;
  @tracked searchQuery = '';
  @tracked sortBy: ConfigSort = 'name';

  get configurationCountLabel(): string {
    const count = this.args.coachingConfigs.length;

    return `${count} configuration${count === 1 ? '' : 's'}`;
  }

  get filteredConfigs(): CoachingConfig[] {
    const query = this.searchQuery.trim().toLowerCase();

    if (!query) {
      return this.args.coachingConfigs;
    }

    return this.args.coachingConfigs.filter((config) => {
      return config.name.toLowerCase().includes(query);
    });
  }

  get sortedConfigs(): CoachingConfig[] {
    return sortConfigs(this.filteredConfigs, this.sortBy);
  }

  get totalCount(): number {
    return this.sortedConfigs.length;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / PAGE_SIZE));
  }

  get pageStart(): number {
    if (this.totalCount === 0) {
      return 0;
    }

    return (this.currentPage - 1) * PAGE_SIZE + 1;
  }

  get pageEnd(): number {
    return Math.min(this.currentPage * PAGE_SIZE, this.totalCount);
  }

  get visibleConfigs(): CoachingConfig[] {
    const start = (this.currentPage - 1) * PAGE_SIZE;

    return this.sortedConfigs.slice(start, start + PAGE_SIZE);
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, index) => index + 1);
  }

  get canGoPrevious(): boolean {
    return this.currentPage > 1;
  }

  get canGoNext(): boolean {
    return this.currentPage < this.totalPages;
  }

  get sortLabel(): string {
    return this.sortBy === 'name' ? 'Name A–Z' : 'Date added';
  }

  setSearchQuery = (event: Event) => {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    this.searchQuery = target.value;
    this.currentPage = 1;
  };

  selectSort = (sort: ConfigSort, close: () => void) => {
    this.sortBy = sort;
    this.currentPage = 1;
    close();
  };

  goToPage = (page: number) => {
    this.currentPage = Math.min(Math.max(page, 1), this.totalPages);
  };

  goPrevious = () => {
    this.goToPage(this.currentPage - 1);
  };

  goNext = () => {
    this.goToPage(this.currentPage + 1);
  };

  <template>
    <div ...attributes>
      <div
        class="d-flex flex-column flex-sm-row justify-content-between align-items-sm-start gap-3 mb-3"
      >
        <div>
          <h1 class="h2 fw-bold mb-1">Coaching Configurations</h1>
          <p
            class="text-body-secondary mb-0"
          >{{this.configurationCountLabel}}</p>
        </div>

        <IfAbility @ability="coaching-configs" @atLeast="editor">
          <:if>
            <LinkTo
              @route="coaching.configs.config"
              @model="new"
              class="btn btn-primary btn-sm"
            >
              <iconify-icon inline icon="ph:plus-bold" />
              New Configuration
            </LinkTo>
          </:if>
        </IfAbility>
      </div>

      <div class="row g-3 align-items-end mb-5">
        <div class="col-12 col-md">
          <label for="coachingConfigSearch" class="visually-hidden">Search
            configurations</label>
          <div class="input-group">
            <span class="input-group-text bg-white">
              <iconify-icon
                inline
                icon="ph:magnifying-glass"
                class="text-body-secondary"
                aria-hidden="true"
              />
            </span>
            <input
              id="coachingConfigSearch"
              type="search"
              class="form-control"
              placeholder="Search configurations..."
              value={{this.searchQuery}}
              {{on "input" this.setSearchQuery}}
            />
          </div>
        </div>

        <div class="col-12 col-md-auto">
          <label id="coachingConfigSortLabel" class="form-label mb-1">Sort by</label>
          <BsDropdown
            @direction="down"
            class="coaching-config-sort-dropdown"
            aria-labelledby="coachingConfigSortLabel"
            as |dd|
          >
            <dd.toggle
              id="coachingConfigSort"
              class="form-select coaching-config-sort-toggle text-start"
            >
              {{this.sortLabel}}
            </dd.toggle>
            <dd.menu @align="start" class="coaching-config-sort-menu" as |ddm|>
              <div class="coaching-config-sort-menu-panel">
                <ddm.item>
                  <button
                    type="button"
                    class="dropdown-item
                      {{if (eq this.sortBy 'name') 'active'}}"
                    {{on "click" (fn this.selectSort "name" dd.closeDropdown)}}
                  >
                    Name A–Z
                  </button>
                </ddm.item>
                <ddm.item>
                  <button
                    type="button"
                    class="dropdown-item
                      {{if (eq this.sortBy 'dateAdded') 'active'}}"
                    {{on
                      "click"
                      (fn this.selectSort "dateAdded" dd.closeDropdown)
                    }}
                  >
                    Date added
                  </button>
                </ddm.item>
              </div>
            </dd.menu>
          </BsDropdown>
        </div>
      </div>

      {{#if this.visibleConfigs.length}}
        <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
          {{#each this.visibleConfigs as |config|}}
            <div class="col">
              <CoachingConfigRow @coachingConfig={{config}} />
            </div>
          {{/each}}
        </div>
      {{else if @coachingConfigs.length}}
        <div class="card border shadow-sm rounded-3">
          <div class="card-body text-center py-5">
            <iconify-icon
              inline
              icon="ph:magnifying-glass"
              class="fs-1 text-body-secondary mb-3"
              aria-hidden="true"
            />
            <h2 class="h5 fw-bold mb-2">No configurations found</h2>
            <p class="text-body-secondary small mb-0">
              Try a different search term.
            </p>
          </div>
        </div>
      {{else}}
        <div class="card border shadow-sm rounded-3">
          <div class="card-body text-center py-5">
            <iconify-icon
              inline
              icon="ph:gear"
              class="fs-1 text-body-secondary mb-3"
              aria-hidden="true"
            />
            <h2 class="h5 fw-bold mb-2">No coaching configurations yet</h2>
            <p class="text-body-secondary small mb-4">
              Create a configuration template to apply to cohort coaching
              programs.
            </p>
            <IfAbility @ability="coaching-configs" @atLeast="editor">
              <:if>
                <LinkTo
                  @route="coaching.configs.config"
                  @model="new"
                  class="btn btn-primary fw-semibold"
                >
                  Create the first one
                </LinkTo>
              </:if>
            </IfAbility>
          </div>
        </div>
      {{/if}}

      {{#if this.totalCount}}
        <div
          class="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3 mt-4"
        >
          <p class="text-body-secondary small mb-0">
            Showing
            {{this.pageStart}}–{{this.pageEnd}}
            of
            {{this.totalCount}}
          </p>

          {{#if (gt this.totalPages 1)}}
            <nav aria-label="Configuration pagination">
              <div class="d-flex align-items-center gap-2">
                <button
                  type="button"
                  class="btn coaching-config-pagination-btn d-inline-flex align-items-center justify-content-center rounded-1 lh-1 border bg-white
                    {{if
                      this.canGoPrevious
                      'text-body-emphasis'
                      'text-body-secondary'
                    }}"
                  aria-label="Previous page"
                  disabled={{unless this.canGoPrevious true}}
                  {{on "click" this.goPrevious}}
                >
                  <iconify-icon
                    inline
                    icon="ph:caret-left"
                    aria-hidden="true"
                  />
                </button>

                {{#each this.pageNumbers as |page|}}
                  <button
                    type="button"
                    class="btn coaching-config-pagination-btn d-inline-flex align-items-center justify-content-center rounded-1 lh-1 fw-semibold
                      {{if
                        (eq page this.currentPage)
                        'btn-primary border-primary'
                        'bg-white border text-body-emphasis'
                      }}"
                    aria-current="{{if (eq page this.currentPage) 'page'}}"
                    {{on "click" (fn this.goToPage page)}}
                  >
                    {{page}}
                  </button>
                {{/each}}

                <button
                  type="button"
                  class="btn coaching-config-pagination-btn d-inline-flex align-items-center justify-content-center rounded-1 lh-1 border bg-white
                    {{if
                      this.canGoNext
                      'text-body-emphasis'
                      'text-body-secondary'
                    }}"
                  aria-label="Next page"
                  disabled={{unless this.canGoNext true}}
                  {{on "click" this.goNext}}
                >
                  <iconify-icon
                    inline
                    icon="ph:caret-right"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </nav>
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
